import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { getThemePaths } from '../common/auth/themePaths';

const GRADIENT_LIGHT_FALLBACK = 'linear-gradient(135deg, #e6f0ff 0%, #d0e1ff 25%, #ffffff 50%, #e6f0ff 100%)';
const GRADIENT_DARK_FALLBACK = 'linear-gradient(135deg, #020617 0%, #0b1b33 45%, #102542 100%)';

const GRADIENT_LIGHT_OVERLAY = 'linear-gradient(135deg, rgba(230, 240, 255, 0.18) 0%, rgba(208, 225, 255, 0.16) 25%, rgba(255, 255, 255, 0.12) 50%, rgba(230, 240, 255, 0.18) 100%)';
const GRADIENT_DARK_OVERLAY = 'linear-gradient(135deg, rgba(2, 6, 23, 0.22) 0%, rgba(11, 27, 51, 0.2) 45%, rgba(16, 37, 66, 0.18) 100%)';

const backgroundCache = new Map();

const loadImage = (url) => {
  if (!url || typeof window === 'undefined') {
    return Promise.resolve(false);
  }
  if (backgroundCache.has(url)) {
    return Promise.resolve(backgroundCache.get(url));
  }
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      backgroundCache.set(url, true);
      resolve(true);
    };
    img.onerror = () => {
      backgroundCache.set(url, false);
      resolve(false);
    };
    img.src = url;
  });
};

const LoginLayout = ({ children }) => {
  const theme = useTheme();
  const revenda = useSelector((state) => state.session.revenda);
  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const candidates = useMemo(() => {
    const paths = getThemePaths(revenda);
    const isDark = theme.palette.mode === 'dark';
    const themeParam = isDark ? 'dark' : 'light';
    const code = revenda?.code;
    const list = [];

    if (code) {
      list.push(`/api/revendas/background/${code}?theme=${themeParam}`);
    }

    const staticPath = isDark ? paths.backgroundDark : paths.backgroundLight;
    if (staticPath) {
      list.push(staticPath);
    }

    return Array.from(new Set(list));
  }, [revenda, theme.palette.mode]);

  useEffect(() => {
    let cancelled = false;

    const resolveBackground = async () => {
      for (const candidate of candidates) {
        const normalized = normalize(candidate);
        if (!normalized) {
          continue;
        }
        const ok = await loadImage(normalized);
        if (cancelled) {
          return;
        }
        if (ok) {
          setBackgroundUrl(normalized);
          return;
        }
      }
      if (!cancelled) {
        setBackgroundUrl(null);
      }
    };

    resolveBackground();
    return () => {
      cancelled = true;
    };
  }, [candidates]);

  const hasBackground = Boolean(backgroundUrl);
  const gradientLayer = theme.palette.mode === 'dark'
    ? (hasBackground ? GRADIENT_DARK_OVERLAY : GRADIENT_DARK_FALLBACK)
    : (hasBackground ? GRADIENT_LIGHT_OVERLAY : GRADIENT_LIGHT_FALLBACK);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: 2, sm: 3 },
        backgroundColor: theme.palette.background.default,
        backgroundImage: backgroundUrl
          ? `${gradientLayer}, url(${backgroundUrl})`
          : gradientLayer,
        backgroundBlendMode: backgroundUrl ? 'overlay, normal' : 'normal',
        backgroundSize: backgroundUrl ? 'cover, cover' : 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        transition: 'background-image 320ms ease, background-color 320ms ease',
      }}
    >
      {children}
    </Box>
  );
};

function isAbsolute(url) {
  return /^https?:\/\//i.test(url) || url.startsWith('/');
}

function normalize(url) {
  if (!url) {
    return null;
  }
  if (isAbsolute(url)) {
    return url;
  }
  return `/${url.replace(/^\/+/, '')}`;
}

export default LoginLayout;
